import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';

import { Spinner } from 'admin-components/components';
import AlertBox from 'components/ui/AlertBox';
import { Cell, TableRow } from 'components/ui/StyledTable';
import { Column, Row } from 'components/shared/Layout';

import { isActiveCase } from './SalesforceCasesDetails.utils';
import { CasesTable, CasesTableHeader, CaseRow, MobileLabel, Status } from './SalesforceCasesDetails.styles';

const DATE_FORMAT = 'Do MMMM YYYY [at] HH:mm';

const SalesforceCasesDetails = ({ cases, loading, error }) => {
  if (loading) {
    return (
      <Row span={16} height="10vh" data-testid="full-page-spinner">
        <Column justifyContent="center" alignItems="center">
          <Spinner />
        </Column>
      </Row>
    );
  }

  if (error) {
    return (
      <AlertBox dataCy="salesforce-cases-error" variant={AlertBox.variants.ERROR}>
        <div>
          <FormattedMessage id="errorBoundary.somethingWrong" />
        </div>
        {error.graphQLErrors.map(({ message }) => (
          <div key={message}>{message}</div>
        ))}
      </AlertBox>
    );
  }

  return (
    <CasesTable marginTop="10px" data-cy="salesforce-cases">
      <CasesTableHeader data-testid="salesforce-cases-header">
        <Cell span={1}>
          <FormattedMessage id="notes.salesforce.caseNumber" />
        </Cell>
        <Cell span={1}>
          <FormattedMessage id="notes.salesforce.creationDate" />
        </Cell>
        <Cell span={1}>
          <FormattedMessage id="notes.salesforce.status" />
        </Cell>
        <Cell span={1}>
          <FormattedMessage id="notes.salesforce.origin" />
        </Cell>
        <Cell span={1}>
          <FormattedMessage id="notes.salesforce.subject" />
        </Cell>
      </CasesTableHeader>

      {cases.length ? (
        cases.map(({ id, caseNumber, status, origin, subject, createdDate, url }) => (
          <CaseRow key={id} data-testid="salesforce-case">
            <Cell span={1}>
              <MobileLabel>
                <FormattedMessage id="notes.salesforce.caseNumber" />
              </MobileLabel>
              <a href={url} target="_blank" rel="noreferrer">
                {caseNumber}
              </a>
            </Cell>
            <Cell span={1}>
              <MobileLabel>
                <FormattedMessage id="notes.salesforce.creationDate" />
              </MobileLabel>
              {moment(createdDate).format(DATE_FORMAT)}
            </Cell>
            <Cell span={1}>
              <MobileLabel>
                <FormattedMessage id="notes.salesforce.status" />
              </MobileLabel>
              <Status $active={isActiveCase({ status })}>{status}</Status>
            </Cell>
            <Cell span={1}>
              <MobileLabel>
                <FormattedMessage id="notes.salesforce.origin" />
              </MobileLabel>
              {origin}
            </Cell>
            <Cell span={1}>
              <MobileLabel>
                <FormattedMessage id="notes.salesforce.subject" />
              </MobileLabel>
              {subject}
            </Cell>
          </CaseRow>
        ))
      ) : (
        <TableRow columns={14} data-cy="salesforce-cases-empty">
          <Cell span={14}>
            <FormattedMessage id="notes.salesforce.noCases" />
          </Cell>
        </TableRow>
      )}
    </CasesTable>
  );
};

SalesforceCasesDetails.propTypes = {
  cases: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      caseNumber: PropTypes.string.isRequired,
      status: PropTypes.string,
      origin: PropTypes.string,
      subject: PropTypes.string,
      createdDate: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.shape({
    graphQLErrors: PropTypes.arrayOf(
      PropTypes.shape({
        message: PropTypes.string.isRequired
      })
    ).isRequired
  })
};

export default SalesforceCasesDetails;
